export type AlgoliaRequestWithParams = { params?: Record<string, unknown> };
export type UrlRefinement = { attribute: string; values: string[] };

const normalizeRefinementToken = (attribute: string, value: string): string => `${attribute}:${value}`.toLowerCase();

const collectFacetFilterTokens = (facetFilters: unknown, tokens = new Set<string>()): Set<string> => {
    if (typeof facetFilters === 'string') {
        tokens.add(facetFilters.trim().toLowerCase());
        return tokens;
    }

    if (Array.isArray(facetFilters)) {
        facetFilters.forEach(facetFilter => collectFacetFilterTokens(facetFilter, tokens));
    }

    return tokens;
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const filtersExpressionHasRefinement = (filters: unknown, attribute: string, value: string): boolean => {
    if (typeof filters !== 'string') {
        return false;
    }

    const escapedAttribute = escapeRegExp(attribute);
    const escapedValue = escapeRegExp(value);
    const filterPattern = new RegExp(
        `(?:^|[\\s(])${escapedAttribute}\\s*:\\s*(?:"${escapedValue}"|${escapedValue})(?=$|[\\s)])`,
        'i'
    );

    return filterPattern.test(filters);
};

/**
 * Checks whether an Algolia request already includes every URL-derived facet refinement.
 *
 * A refinement is considered present only when the request contains an exact `attribute:value`
 * token in `facetFilters`, or an equivalent exact expression in `filters`. This avoids treating a
 * shorter URL facet value as present when it only appears inside a longer value.
 *
 * @param request - Algolia request emitted by InstantSearch.
 * @param refinements - URL-derived refinements that must be preserved on the initial request.
 * @returns Whether the request already contains all required URL refinements.
 */
export const requestSatisfiesUrlRefinements = (
    request: AlgoliaRequestWithParams,
    refinements: readonly UrlRefinement[]
): boolean => {
    if (!refinements.length) {
        return true;
    }

    const params = request.params ?? {};
    const facetFilterTokens = collectFacetFilterTokens(params.facetFilters);

    return refinements.every(({ attribute, values }) =>
        values.every(value => {
            const refinementToken = normalizeRefinementToken(attribute, value);

            return (
                facetFilterTokens.has(refinementToken) ||
                filtersExpressionHasRefinement(params.filters, attribute, value)
            );
        })
    );
};

/**
 * Adds missing URL-derived refinements to an Algolia request's `facetFilters`.
 *
 * Requests that already satisfy all URL refinements are returned unchanged. Otherwise, each URL
 * refinement is appended as an Algolia facet filter while preserving any existing request params and
 * facet filters.
 *
 * @param request - Algolia request to protect before the initial client-side search runs.
 * @param refinements - URL-derived refinements to add when absent from the request.
 * @returns The original request when no changes are needed, or a copy with merged `facetFilters`.
 */
export const addUrlRefinementsToRequest = <T extends AlgoliaRequestWithParams>(
    request: T,
    refinements: readonly UrlRefinement[]
): T => {
    if (requestSatisfiesUrlRefinements(request, refinements)) {
        return request;
    }

    const urlFacetFilters = refinements.map(({ attribute, values }) => {
        const facetValues = values.map(value => `${attribute}:${value}`);
        return facetValues.length === 1 ? facetValues[0] : facetValues;
    });
    const existingFacetFilters = request.params?.facetFilters;
    let facetFilters: typeof urlFacetFilters | (typeof existingFacetFilters | string)[];

    if (existingFacetFilters === undefined) {
        facetFilters = urlFacetFilters;
    } else if (Array.isArray(existingFacetFilters)) {
        facetFilters = [...existingFacetFilters, ...urlFacetFilters];
    } else {
        facetFilters = [existingFacetFilters, ...urlFacetFilters];
    }

    return {
        ...request,
        params: {
            ...request.params,
            facetFilters,
        },
    };
};
